import { lazy, ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-at";

/**
 * Lazy-load a route chunk. When a deploy replaces the JS files, an open tab can
 * still reference an old chunk URL, which fails with "Failed to fetch
 * dynamically imported module" and a blank screen. We retry once, then reload
 * the page (at most once per minute) so the browser picks up the new build.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      // Retry once: transient network hiccup.
      try {
        return await factory();
      } catch (retryError) {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Keep the promise pending while the page reloads.
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
